import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { nossaHistoriaApi, type TimelineEvent, type TeamMember, type NossaHistoriaContent } from "@/integrations/mysql/client";
import { toast } from "@/hooks/use-toast";

export type { TimelineEvent, TeamMember, NossaHistoriaContent };

export function useNossaHistoria() {
  const queryClient = useQueryClient();

  // ── Timeline ─────────────────────────────────────────────────────────────────
  const { data: timelineEvents, isLoading: timelineLoading } = useQuery({
    queryKey: ['timeline-events'],
    queryFn:  () => nossaHistoriaApi.timeline(),
  });

  const updateTimelineEvent = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<TimelineEvent> }) =>
      nossaHistoriaApi.updateTimeline(id, updates),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['timeline-events'] }); toast({ title: "Evento atualizado!" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const createTimelineEvent = useMutation({
    mutationFn: (data: Omit<TimelineEvent, 'id'>) => nossaHistoriaApi.createTimeline(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['timeline-events'] }); toast({ title: "Evento criado!" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteTimelineEvent = useMutation({
    mutationFn: (id: string) => nossaHistoriaApi.deleteTimeline(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['timeline-events'] }); toast({ title: "Evento deletado!" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  // ── Team ──────────────────────────────────────────────────────────────────────
  const { data: teamMembers, isLoading: teamLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn:  () => nossaHistoriaApi.team(),
  });

  const updateTeamMember = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<TeamMember> }) =>
      nossaHistoriaApi.updateTeam(id, updates),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['team-members'] }); toast({ title: "Membro atualizado!" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const createTeamMember = useMutation({
    mutationFn: (data: Omit<TeamMember, 'id'>) => nossaHistoriaApi.createTeam(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['team-members'] }); toast({ title: "Membro criado!" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteTeamMember = useMutation({
    mutationFn: (id: string) => nossaHistoriaApi.deleteTeam(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['team-members'] }); toast({ title: "Membro deletado!" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  // ── Content ───────────────────────────────────────────────────────────────────
  const { data: content, isLoading: contentLoading } = useQuery({
    queryKey: ['nossa-historia-content'],
    queryFn:  () => nossaHistoriaApi.content(),
  });

  const updateContent = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<NossaHistoriaContent> }) =>
      nossaHistoriaApi.updateContent(id, updates),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['nossa-historia-content'] }); toast({ title: "Conteúdo atualizado!" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const createContent = useMutation({
    mutationFn: (data: Omit<NossaHistoriaContent, 'id'>) => nossaHistoriaApi.createContent(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['nossa-historia-content'] }); },
    onError: (e: Error) => toast({ title: "Erro ao criar conteúdo", description: e.message, variant: "destructive" }),
  });

  return {
    timelineEvents: Array.isArray(timelineEvents) ? timelineEvents : [],
    timelineLoading,
    updateTimelineEvent: updateTimelineEvent.mutate,
    createTimelineEvent: createTimelineEvent.mutate,
    deleteTimelineEvent: deleteTimelineEvent.mutate,

    teamMembers: Array.isArray(teamMembers) ? teamMembers : [],
    teamLoading,
    updateTeamMember: updateTeamMember.mutate,
    createTeamMember: createTeamMember.mutate,
    deleteTeamMember: deleteTeamMember.mutate,

    content, contentLoading,
    updateContent: updateContent.mutate,
    updateContentAsync: updateContent.mutateAsync,
    createContent: createContent.mutateAsync,
  };
}
